/* <product-recommendations> — Shopify Product Recommendations via the
   Section Rendering pattern (sections/product-list.liquid, source:
   'recommendations'). Markup contract:
   - data-url  {{ routes.product_recommendations_url }}?section_id=…&
     product_id=…&limit=…&intent=related
   - closest [data-product-list] is the section wrapper (heading + slider);
     the section renders it `hidden` when the fallback list is empty.
   The fetched HTML is this same section rendered with
   recommendations.performed == true; its <product-recommendations
   data-performed> inner HTML replaces the server-rendered fallback (the
   manual product list) when it contains a slider track, and the wrapper is
   un-hidden. No track in the response AND no fallback cards → the whole
   wrapper collapses (an orphaned heading + padding otherwise renders).
   Lazy-loaded via data-lazy-module; product-list.liquid adds an eager
   <script type="module"> when the wrapper starts hidden, because a
   display:none host never reaches lazyDefine's IntersectionObserver. */

import { lazyDefine } from './critical.js';

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.fetched || this.hasAttribute('data-performed')) return;
    const url = this.dataset.url;
    if (!url) return;
    this.fetched = true;
    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations[data-performed]');
        const wrap = this.closest('[data-product-list]');
        if (fresh && fresh.querySelector('[data-slider-track]')) {
          this.innerHTML = fresh.innerHTML;
          if (wrap) wrap.hidden = false;
          /* New [data-lazy-module] hosts (scroll-slider, product forms) came
             in with the swap — arm their lazy loaders. */
          lazyDefine(this);
        } else if (!this.querySelector('[data-slider-track]') && !window.Shopify?.designMode) {
          /* Zero recommendations and no fallback cards — collapse the whole
             section, heading included. Skipped in the theme editor so the
             section stays visible/selectable there. */
          if (wrap) wrap.hidden = true;
        }
      })
      .catch(() => {
        /* Network/render failure — keep the server-rendered fallback. */
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
